import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Icon } from '../icon/Icon';
import { range } from '../utils/range';

const StyledPagination = styled.ul`
  border-bottom: ${themeGet('pagination.border.bottom')};
  display: inline-block;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  list-style: none;
`;

const PaginationItem = styled.li`
  min-width: ${themeGet('pagination.item.minWidth')};
  font-size: ${themeGet('pagination.item.fontSize')};
  color: ${themeGet('pagination.item.color')};
  background-color: ${themeGet('pagination.item.backgroundColor')};
  border-top: ${themeGet('pagination.item.border.top')};
  border-right: ${themeGet('pagination.item.border.right')};
  border-bottom: ${themeGet('pagination.item.border.bottom')};
  border-left: ${themeGet('pagination.item.border.left')};
  border-radius: ${themeGet('pagination.item.borderRadius')};
  display: inline-block;
  height: 30px;
  line-height: 30px;
  margin-right: 8px;
  text-align: center;
  vertical-align: middle;
  outline: 0;
  cursor: pointer;
  user-select: none;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    color: ${themeGet('pagination.item.hover.color')};
    background-color: ${themeGet('pagination.item.hover.backgroundColor')};
    border-top: ${themeGet('pagination.item.hover.border.top')};
    border-right: ${themeGet('pagination.item.hover.border.right')};
    border-bottom: ${themeGet('pagination.item.hover.border.bottom')};
    border-left: ${themeGet('pagination.item.hover.border.left')};
    border-radius: ${themeGet('pagination.item.hover.borderRadius')};
  }

  ${props =>
    props.active &&
    css`
      && {
        color: ${themeGet('pagination.item.selected.color')};
        background-color: ${themeGet('pagination.item.selected.backgroundColor')};
        border-top: ${themeGet('pagination.item.selected.border.top')};
        border-right: ${themeGet('pagination.item.selected.border.right')};
        border-bottom: ${themeGet('pagination.item.selected.border.bottom')};
        border-left: ${themeGet('pagination.item.selected.border.left')};
        border-radius: ${themeGet('pagination.item.selected.borderRadius')};
        font-weight: 600;
        cursor: default;
      }
    `}
`;

const PaginationArrow = styled.button`
  display: inline-block;
  vertical-align: middle;
  border: none;
  padding: 0;
  margin: 0 8px;
  background: none;
  outline: none;
  cursor: pointer;

  ${Icon} {
    color: ${themeGet('pagination.arrow.color')};
    vertical-align: baseline;

    &:hover {
      color: ${props =>
        props.disabled
          ? themeGet('pagination.arrow.disabled.color')
          : themeGet('pagination.item.hover.color')};
    }

    ${props =>
      props.disabled &&
      css`
        color: ${themeGet('pagination.arrow.disabled.color')};
        cursor: not-allowed;
      `}
  }
`;

const More = styled.span`
  color: ${themeGet('pagination.more.color')};
  display: inline-block;
  width: 100%;
  cursor: default;
`;

export const Pagination = ({ defaultCurrent, current, pageSize = 20, total = 0, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  const [currentPage, setCurrentPage] = useState(current || defaultCurrent || 1);
  const onPageChange = useCallback(
    page => {
      if (onChange) {
        onChange(page);
      }

      if (!current) {
        setCurrentPage(page);
      }
    },
    [onChange, current]
  );

  const goLastPage = useCallback(() => {
    onPageChange(totalPages);
  }, [onPageChange, totalPages]);

  const goFirstPage = useCallback(() => {
    onPageChange(1);
  }, [onPageChange]);

  const goPreviousPage = useCallback(() => {
    onPageChange(currentPage - 1);
  }, [onPageChange, currentPage]);

  const goNextPage = useCallback(() => onPageChange(currentPage + 1), [onPageChange, currentPage]);

  useEffect(() => {
    if (current && current !== currentPage) {
      setCurrentPage(current);
    }
  }, [current, setCurrentPage, currentPage]);

  /**
   * First Page and Last Page is always show.
   * showPages is an array of showing page exclude 1st and last page
   * so showPages min value is 2, max value is (totalPages - 1)
   *
   * ps. range(2, 6) => [2, 3, 4, 5], so the min value pass range is totalPages
   */
  const showPages =
    currentPage <= 4 // 前四頁，都顯示 2 ~ 5
      ? range(2, Math.min(totalPages, 6)) // totalPages 小於等於 5 就顯示 2 ~ (totalPages - 1)
      : currentPage >= totalPages - 3 // 最後四頁，都顯示 最後四頁, ex: 總頁數 30，若 current page 大於等於 26 就顯示 25 ~ 29
      ? range(Math.max(2, totalPages - 4), totalPages)
      : range(Math.max(2, currentPage - 1), Math.min(totalPages, currentPage + 2));

  return (
    <React.Fragment>
      <PaginationArrow disabled={currentPage <= 1} onClick={goPreviousPage}>
        <Icon name="ARROW_LEFT" />
      </PaginationArrow>
      <StyledPagination>
        <PaginationItem active={currentPage === 1} onClick={goFirstPage}>
          1
        </PaginationItem>
        {showPages[0] >= 3 && (
          <PaginationItem>
            <More>...</More>
          </PaginationItem>
        )}
        {showPages.map(item => (
          <PaginationItem
            active={currentPage === item}
            key={`${item}-pagination`}
            onClick={() => {
              onPageChange(item);
            }}
          >
            {item}
          </PaginationItem>
        ))}
        {showPages[showPages.length - 1] <= totalPages - 2 && (
          <PaginationItem>
            <More>...</More>
          </PaginationItem>
        )}

        {totalPages !== 1 && (
          <PaginationItem active={currentPage === totalPages} key="1-pagination" onClick={goLastPage}>
            {totalPages}
          </PaginationItem>
        )}
      </StyledPagination>
      <PaginationArrow disabled={currentPage >= totalPages} onClick={goNextPage}>
        <Icon name="ARROW_RIGHT" />
      </PaginationArrow>
    </React.Fragment>
  );
};
